import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Check, Sparkles, X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { setCardListContext } from "@/lib/cardContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import Seo from "@/components/Seo";

// Rarity filter buckets — each one maps a UI pill to one or more raw rarity strings
// returned by the pokemontcg / TCGdex APIs. The matching is case-insensitive and
// done via "includes" on the lowercased rarity string for resilience to new ones.
const RARITY_GROUPS = [
  { key: "common",     match: (r) => r === "common" },
  { key: "uncommon",   match: (r) => r === "uncommon" },
  { key: "rare",       match: (r) => r === "rare" || r === "rare holo" },
  { key: "ex_gx",      match: (r) => /\bex\b|\bgx\b/.test(r) || r === "double rare" },
  { key: "v_vmax",     match: (r) => /\bvmax\b|\bvstar\b/.test(r) || /^rare holo v\b/.test(r) },
  { key: "ultra",      match: (r) => r.includes("ultra") || r === "illustration rare" || r === "trainer gallery rare holo" },
  { key: "secret",     match: (r) => r.includes("secret") || r.includes("rainbow") || r.includes("hyper") || r.includes("special illustration") },
  { key: "promo",      match: (r) => r.includes("promo") },
];

function pickCardPrice(c) {
  const cm = c?.cardmarket?.prices || {};
  const v = cm.trendPrice || cm.averageSellPrice || cm.lowPrice;
  if (v && v > 0) return { value: v, currency: "EUR" };
  const tp = c?.tcgplayer?.prices || {};
  for (const f of ["normal", "holofoil", "reverseHolofoil", "1stEditionHolofoil"]) {
    const m = tp[f]?.market;
    if (m && m > 0 && m < 999) return { value: m, currency: "USD" };
  }
  return null;
}

const RARITY_PILL = {
  "Common": "bg-slate-100 text-slate-500",
  "Uncommon": "bg-emerald-50 text-emerald-700",
  "Rare": "bg-amber-50 text-amber-700",
  "Rare Holo": "bg-amber-100 text-amber-800",
  "Rare Ultra": "bg-rose-50 text-rose-700",
  "Rare Secret": "bg-fuchsia-50 text-fuchsia-700",
  "Rare Holo GX": "bg-rose-50 text-rose-700",
  "Rare Holo EX": "bg-rose-50 text-rose-700",
  "Rare Holo V": "bg-rose-50 text-rose-700",
  "Rare Holo VMAX": "bg-rose-50 text-rose-700",
  "Rare Rainbow": "bg-fuchsia-50 text-fuchsia-700",
  "Promo": "bg-blue-50 text-blue-700",
};

export default function SearchPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { format } = useCurrency();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ownedIds, setOwnedIds] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [activeRarities, setActiveRarities] = useState(new Set());

  const runSearch = async (query) => {
    if (!query) {
      setCards([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/cards/search", {
        params: { q: query, pageSize: 120, lang: i18n.language },
      });
      // Sort by set release date DESC (most recent first), then by card number
      const sorted = (data.data || []).slice().sort((a, b) => {
        const dateA = a.set?.releaseDate || a.set?.release_date || "1900-01-01";
        const dateB = b.set?.releaseDate || b.set?.release_date || "1900-01-01";
        if (dateA !== dateB) return dateB.localeCompare(dateA);
        return (a.number || "").localeCompare(b.number || "");
      });
      setCards(sorted);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialQ = params.get("q");
    if (initialQ) {
      setQ(initialQ);
      runSearch(initialQ);
    }
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    if (!user) {
      setOwnedIds(new Set());
      return;
    }
    api.get("/collection/owned-ids", { params: { status: "owned" } })
      .then((r) => setOwnedIds(new Set(r.data.card_ids || [])))
      .catch(() => {});
  }, [user]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      setParams({ q: q.trim() });
      runSearch(q.trim());
    }
  };

  const visibleCards = useMemo(() => {
    let list = cards;
    if (ownedOnly) list = list.filter((c) => ownedIds.has(c.id));
    if (activeRarities.size > 0) {
      list = list.filter((c) => {
        const r = (c.rarity || "").toLowerCase();
        if (!r) return false;
        for (const key of activeRarities) {
          const g = RARITY_GROUPS.find((x) => x.key === key);
          if (g && g.match(r)) return true;
        }
        return false;
      });
    }
    return list;
  }, [cards, ownedOnly, ownedIds, activeRarities]);

  // For each rarity group, count how many cards in current `cards` match it.
  // This is used to render pills and disable the empty ones.
  const rarityCounts = useMemo(() => {
    const map = Object.fromEntries(RARITY_GROUPS.map((g) => [g.key, 0]));
    cards.forEach((c) => {
      const r = (c.rarity || "").toLowerCase();
      if (!r) return;
      RARITY_GROUPS.forEach((g) => {
        if (g.match(r)) map[g.key] += 1;
      });
    });
    return map;
  }, [cards]);

  const toggleRarity = (key) => {
    setActiveRarities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearRarities = () => setActiveRarities(new Set());

  // Group cards by set (preserving release-date-desc ordering of cards)
  const grouped = useMemo(() => {
    const out = [];
    const idx = new Map();
    visibleCards.forEach((c) => {
      const sid = c.set?.id || "unknown";
      if (!idx.has(sid)) {
        idx.set(sid, out.length);
        out.push({
          set_id: sid,
          set_name: c.set?.name || "—",
          logo: c.set?.images?.logo,
          releaseDate: c.set?.releaseDate || c.set?.release_date || "",
          cards: [],
        });
      }
      out[idx.get(sid)].cards.push(c);
    });
    return out;
  }, [visibleCards]);

  const ownedCount = useMemo(() => cards.filter((c) => ownedIds.has(c.id)).length, [cards, ownedIds]);

  const toggleOwn = async (e, card) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error(t("set_detail.login_required", "Connectez-vous pour gérer votre collection."));
      return;
    }
    setBusyId(card.id);
    try {
      const res = await api.post("/collection/toggle", {
        card_id: card.id,
        status: "owned",
        quantity: 1,
        language: "en",
        condition: "near_mint",
      });
      setOwnedIds((prev) => {
        const next = new Set(prev);
        if (res.data.owned) next.add(card.id);
        else next.delete(card.id);
        return next;
      });
      toast.success(
        res.data.owned
          ? t("set_detail.added_owned", "{{name}} ajoutée à votre collection", { name: card.name })
          : t("set_detail.removed_owned", "{{name}} retirée de votre collection", { name: card.name })
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <Seo title={q ? t("search.title_with_q", "Recherche: {{q}}", { q }) : t("search.title", "Recherche")} />

      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#E11D48] mb-2 flex items-center gap-2">
          <SearchIcon className="w-3.5 h-3.5" /> {t("search.eyebrow", "Rechercher")}
        </div>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900">
          {t("search.title", "Rechercher une carte")}
        </h1>
        <p className="text-slate-500 mt-2 text-sm max-w-2xl">
          {t("search.subtitle", "Tapez un nom Pokémon (Dracaufeu, Bulbizarre, Pikachu...) — les résultats sont triés de la sortie la plus récente à la plus ancienne.")}
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-wrap gap-3 items-center mb-8" data-testid="search-form">
        <div className="relative flex-1 min-w-[260px] max-w-2xl">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder", "Charizard, Pikachu, Mew…")}
            className="w-full pl-9"
            data-testid="search-page-input"
          />
        </div>
        <button type="submit" className="btn-primary text-xs uppercase tracking-wider" data-testid="search-submit-btn">
          {t("search.cta", "Rechercher")}
        </button>
        {user && cards.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none ml-auto">
            <input
              type="checkbox"
              checked={ownedOnly}
              onChange={(e) => setOwnedOnly(e.target.checked)}
              className="w-4 h-4 accent-[#E11D48]"
              data-testid="search-owned-only"
            />
            {t("search.owned_only", "Possédées uniquement")}
            {ownedCount > 0 && <span className="text-xs text-slate-400">({ownedCount})</span>}
          </label>
        )}
      </form>

      {cards.length > 0 && (
        <div className="mb-8" data-testid="rarity-filter-bar">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-[#E11D48]" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-700">
              {t("search.filter_rarity", "Filtrer par rareté")}
            </span>
            {activeRarities.size > 0 && (
              <button
                type="button"
                onClick={clearRarities}
                data-testid="rarity-clear-btn"
                className="ml-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-400 hover:text-[#E11D48] transition-colors"
              >
                <X className="w-3 h-3" />
                {t("search.clear", "Réinitialiser")}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {RARITY_GROUPS.map((g) => {
              const count = rarityCounts[g.key];
              const active = activeRarities.has(g.key);
              const disabled = count === 0;
              return (
                <button
                  type="button"
                  key={g.key}
                  data-testid={`rarity-pill-${g.key}`}
                  disabled={disabled}
                  onClick={() => toggleRarity(g.key)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wide transition-all
                    ${active
                      ? "bg-[#E11D48] text-white border-[#E11D48] shadow-sm"
                      : disabled
                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                        : "bg-white text-slate-600 border-slate-200 hover:border-[#E11D48] hover:text-[#E11D48]"}
                  `}
                >
                  {t(`search.rarity.${g.key}`)}
                  <span className={`ml-1.5 text-[10px] font-mono ${active ? "text-white/80" : "text-slate-400"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-400 py-20 text-sm">{t("common.loading", "Loading…")}</div>
      ) : visibleCards.length === 0 ? (
        q ? (
          <div className="text-center text-slate-400 py-20 text-sm" data-testid="search-empty">
            {activeRarities.size > 0
              ? t("search.empty_filtered", "Aucune carte ne correspond à ces filtres de rareté.")
              : t("search.empty", "Aucune carte trouvée pour « {{q}} »", { q })}
          </div>
        ) : (
          <div className="text-center text-slate-400 py-20 text-sm">
            {t("search.start", "Tapez un nom pour commencer.")}
          </div>
        )
      ) : (
        <div className="space-y-10">
          <div className="text-xs text-slate-400">
            {t("search.result_count", "{{n}} résultats", { n: visibleCards.length })}
            {q && cards.length > 0 && (
              <span className="ml-2 text-slate-300">
                · {t("search.sorted_desc", "tri : du plus récent au plus ancien")}
              </span>
            )}
          </div>

          {grouped.map((g, gi) => (
            <motion.div
              key={g.set_id + "-" + gi}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(gi, 6) * 0.05 }}
            >
              <Link to={`/sets/${g.set_id}`} className="flex items-center gap-3 mb-3 group">
                {g.logo && <img src={g.logo} alt={g.set_name} className="h-9 max-w-[140px] object-contain" />}
                <div>
                  <div className="font-heading font-bold text-lg text-slate-900 group-hover:text-[#E11D48] transition-colors">
                    {g.set_name}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono">{g.releaseDate || "—"} · {g.cards.length} {t("set_detail.cards", "cartes")}</div>
                </div>
              </Link>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {g.cards.map((c) => {
                  const owned = ownedIds.has(c.id);
                  const price = pickCardPrice(c);
                  const rarityCls = RARITY_PILL[c.rarity] || "bg-slate-100 text-slate-500";
                  return (
                    <div key={c.id} className="relative">
                      <button
                        type="button"
                        data-testid={`search-own-${c.id}`}
                        onClick={(e) => toggleOwn(e, c)}
                        disabled={busyId === c.id}
                        title={owned ? t("set_detail.owned_yes", "Vous possédez cette carte") : t("set_detail.owned_no", "Cocher pour ajouter à votre collection")}
                        className={`absolute top-2 right-2 z-10 w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all shadow-md ${
                          owned
                            ? "bg-white border-white text-[#E11D48] scale-100"
                            : "bg-white/80 border-slate-200 text-transparent hover:border-[#E11D48] hover:text-[#E11D48] hover:scale-110"
                        } ${busyId === c.id ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
                      >
                        <Check className="w-5 h-5" strokeWidth={3.5} />
                      </button>

                      <Link
                        to={`/cards/${c.id}`}
                        onClick={() => setCardListContext(
                          visibleCards.map((vc) => vc.id),
                          { url: `/search?q=${encodeURIComponent(q)}`, label: q },
                        )}
                        data-testid={`search-tile-${c.id}`}
                        className={`block flat-card overflow-hidden group transition-all hover:-translate-y-1 hover:shadow-lg ${
                          owned ? "ring-2 ring-[#E11D48]/40" : ""
                        }`}
                      >
                        <div className="aspect-[2.5/3.5] bg-slate-50 overflow-hidden">
                          {c.images?.small ? (
                            <img src={c.images.small} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">—</div>
                          )}
                        </div>
                        <div className="p-2.5">
                          <div className="font-heading font-bold text-sm leading-tight text-slate-900 truncate">{c.name}</div>
                          <div className="flex justify-between items-center mt-1.5">
                            <span className="text-[11px] text-slate-400 font-mono">#{c.number}</span>
                            {c.rarity && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${rarityCls}`}>
                                {c.rarity.replace("Rare ", "")}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex items-center justify-between text-sm">
                            {price ? (
                              <span className="font-bold text-slate-900">{format(price.value, price.currency)}</span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                            {owned && (
                              <span className="text-[10px] uppercase tracking-wider font-bold text-[#E11D48]">
                                {t("set_detail.owned_badge", "Possédée")}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
